/**
 * WITS Price Chart Component
 *
 * Real-time electricity price visualization using Recharts.
 * Features:
 * - Multiple node comparison
 * - Time range selection
 * - Price statistics
 * - Auto-refresh with WebSocket
 */

import React, { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from 'recharts';
import { queryKeys, useWitsPrices } from '../hooks/useApi';
import { api } from '../services/api';
import { useLiveDataSocket } from '../hooks/useWebSocket';
import DataBadge from './DataBadge';
import DataProvenanceRow from './DataProvenanceRow';

// =============================================================================
// Types
// =============================================================================

interface NodalPrice {
  node_code: string;
  price_nzd_mwh: number;
  timestamp: string;
  trading_period: number;
}

interface PriceChartProps {
  nodeCodes?: string[];
  height?: number;
  showStatistics?: boolean;
  autoRefresh?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const NODE_COLORS: Record<string, string> = {
  BEN2201: '#3b82f6', // Blue - Benmore
  HAY2201: '#10b981', // Green - Haywards
  OTA2201: '#f59e0b', // Orange - Otahuhu
  ISL2201: '#8b5cf6', // Purple - Islington
  TWI2201: '#ef4444', // Red - Twizel
  MAT1101: '#06b6d4', // Cyan - Matahina
  WKM2201: '#ec4899', // Pink - Waikamaka
  HLY2201: '#84cc16', // Lime - Huntly
};

const DEFAULT_NODES: string[] = [];
const PREFERRED_NODES = ['BEN2201', 'HAY2201', 'OTA2201', 'ISL2201'] as const;

// =============================================================================
// Helper Functions
// =============================================================================

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' });
}

function formatPrice(price: number): string {
  return `$${price.toFixed(2)}/MWh`;
}

function calculateStats(prices: number[]) {
  if (prices.length === 0) return { min: 0, max: 0, avg: 0, std: 0 };
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
  const variance = prices.reduce((sum, p) => sum + (p - avg) ** 2, 0) / prices.length;
  const std = Math.sqrt(variance);
  return { min, max, avg, std };
}

// =============================================================================
// Component
// =============================================================================

export const PriceChart: React.FC<PriceChartProps> = ({
  nodeCodes = DEFAULT_NODES,
  height = 400,
  showStatistics = true,
  autoRefresh = true,
}) => {
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'1h' | '6h' | '24h' | '7d'>('24h');

  // Fetch current prices
  const {
    data: currentPricesResponse,
    isLoading: isCurrentLoading,
    error: currentError,
  } = useWitsPrices(nodeCodes);
  const currentPrices = currentPricesResponse?.prices || [];

  const resolvedNodeCodes = useMemo(() => {
    if (nodeCodes.length > 0) return nodeCodes;
    const discovered = Array.from(
      new Set((currentPrices || []).map((p: NodalPrice) => p.node_code).filter(Boolean))
    ).sort();
    if (discovered.length === 0) return DEFAULT_NODES;
    const preferred = PREFERRED_NODES.filter((node) => discovered.includes(node));
    return (preferred.length > 0 ? preferred : discovered.slice(0, 4)) as string[];
  }, [nodeCodes, currentPrices]);

  // Fetch price history for each node
  const hours = timeRange === '1h' ? 1 : timeRange === '6h' ? 6 : timeRange === '24h' ? 24 : 168;
  const historyQueries = useQueries({
    queries: resolvedNodeCodes.map((node) => ({
      queryKey: [...queryKeys.witsPriceHistory(node), hours],
      queryFn: () => api.getWitsPriceHistory(node, hours),
      enabled: Boolean(node),
      staleTime: 5 * 60_000,
    })),
  });
  const historyLoading = historyQueries.some((query) => query.isLoading);
  const historyError = historyQueries.find((query) => query.isError)?.error;

  // Live data WebSocket for real-time updates
  const { isConnected } = useLiveDataSocket({
    autoSubscribe: autoRefresh ? ['prices'] : [],
    enabled: autoRefresh,
  });

  // Transform data for chart - NO FAKE DATA
  const chartData = useMemo(() => {
    const pricesByTime: Record<string, Record<string, number>> = {};

    const ensureEntry = (timeKey: string) => {
      if (!pricesByTime[timeKey]) {
        pricesByTime[timeKey] = { timestamp: timeKey, time: formatTime(timeKey) } as any;
      }
    };

    historyQueries.forEach((query, idx) => {
      const node = resolvedNodeCodes[idx];
      const history = query.data as { prices?: Array<{ timestamp: string | Date; price_nzd_mwh: number }> } | undefined;
      if (!history?.prices) return;
      history.prices.forEach((point) => {
        const timeKey = typeof point.timestamp === 'string'
          ? point.timestamp
          : new Date(point.timestamp).toISOString();
        ensureEntry(timeKey);
        pricesByTime[timeKey][node] = point.price_nzd_mwh;
      });
    });

    if (currentPrices && currentPrices.length > 0) {
      currentPrices.forEach((price: NodalPrice) => {
        if (!resolvedNodeCodes.includes(price.node_code)) return;
        const timeKey = price.timestamp;
        ensureEntry(timeKey);
        pricesByTime[timeKey][price.node_code] = price.price_nzd_mwh;
      });
    }

    return Object.values(pricesByTime).sort(
      (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [currentPrices, historyQueries, resolvedNodeCodes]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const stats: Record<string, { min: number; max: number; avg: number; std: number }> = {};

    resolvedNodeCodes.forEach((node) => {
      const prices = chartData
        .map((d) => d[node])
        .filter((p) => typeof p === 'number') as number[];
      stats[node] = calculateStats(prices);
    });

    return stats;
  }, [chartData, resolvedNodeCodes]);

  // Average price for reference line
  const averagePrice = useMemo(() => {
    const allPrices = resolvedNodeCodes.flatMap((node) =>
      chartData.map((d) => d[node]).filter((p) => typeof p === 'number')
    );
    return calculateStats(allPrices as number[]).avg;
  }, [chartData, resolvedNodeCodes]);

  const isLoading = (isCurrentLoading || historyLoading) && chartData.length === 0;

  if (isLoading) {
    return (
      <div className="bg-gray-800 rounded-lg p-4 animate-pulse" style={{ height }}>
        <div className="h-full flex items-center justify-center text-gray-400">
          Loading price data...
        </div>
      </div>
    );
  }

  // Show honest "no data" message instead of fake data
  if (chartData.length === 0) {
    const errorMessage =
      (currentError as Error | undefined)?.message ||
      (historyError as Error | undefined)?.message ||
      'Unable to fetch real-time prices from WITS API.';
    return (
      <div className="bg-gray-800 rounded-lg p-4" style={{ height }}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-white">Wholesale Electricity Prices</h3>
            <DataBadge tier="LIVE" />
          </div>
        </div>
        <DataProvenanceRow
          tier="LIVE"
          source={currentPricesResponse?.provenance?.source || currentPricesResponse?.source || 'WITS'}
          asOf={currentPricesResponse?.provenance?.as_of || currentPricesResponse?.timestamp}
          generatedAt={currentPricesResponse?.provenance?.generated_at}
          refresh={autoRefresh ? 'WS + poll' : 'poll'}
          inputs={currentPricesResponse?.provenance?.inputs || { node_codes: nodeCodes }}
          warnings={currentPricesResponse?.provenance?.warnings || null}
          className="mb-3"
          compact
        />
        <div className="h-full flex flex-col items-center justify-center text-gray-400">
          <svg
            className="w-16 h-16 mb-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className="text-lg font-medium mb-2">No Price Data Available</p>
          <p className="text-sm text-center max-w-md">
            {errorMessage}
            <br />
            Check WITS API credentials or network connection.
          </p>
          <p className="text-xs mt-4 text-gray-500">
            This chart only displays real market data - no simulated prices are shown.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-bold text-white">Wholesale Electricity Prices</h3>
          <DataBadge tier="LIVE" />
          {autoRefresh && (
            <span
              className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-500'}`}
              title={isConnected ? 'Live updates active' : 'Disconnected'}
            />
          )}
        </div>

        {/* Time range selector */}
        <div className="flex gap-1">
          {(['1h', '6h', '24h', '7d'] as const).map((range) => (
            <button
              key={range}
              className={`px-2 py-1 text-xs rounded ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              onClick={() => setTimeRange(range)}
            >
              {range}
            </button>
          ))}
        </div>
      </div>
      <DataProvenanceRow
        tier="LIVE"
        source={currentPricesResponse?.provenance?.source || currentPricesResponse?.source || 'WITS'}
        asOf={currentPricesResponse?.provenance?.as_of || currentPricesResponse?.timestamp}
        generatedAt={currentPricesResponse?.provenance?.generated_at}
        refresh={autoRefresh ? 'WS + poll' : 'poll'}
        inputs={currentPricesResponse?.provenance?.inputs || { node_codes: resolvedNodeCodes }}
        warnings={currentPricesResponse?.provenance?.warnings || null}
        className="mb-3"
        compact
      />

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height - 100}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            dataKey="time"
            stroke="#9ca3af"
            fontSize={12}
            tickMargin={8}
          />
          <YAxis
            stroke="#9ca3af"
            fontSize={12}
            tickFormatter={(v) => `$${v}`}
            domain={['auto', 'auto']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1f2937',
              border: '1px solid #374151',
              borderRadius: '8px',
            }}
            labelStyle={{ color: '#fff' }}
            formatter={(value: number, name: string) => [formatPrice(value), name]}
          />
          <Legend
            onClick={(e) => setSelectedNode(e.dataKey === selectedNode ? null : String(e.dataKey || ''))}
            wrapperStyle={{ paddingTop: 10 }}
          />

          {/* Average reference line */}
          <ReferenceLine
            y={averagePrice}
            stroke="#6b7280"
            strokeDasharray="5 5"
            label={{
              value: `Avg: ${formatPrice(averagePrice)}`,
              fill: '#9ca3af',
              fontSize: 10,
              position: 'right',
            }}
          />

          {/* Price lines for each node */}
          {resolvedNodeCodes.map((node) => (
            <Line
              key={node}
              type="monotone"
              dataKey={node}
              stroke={NODE_COLORS[node] || '#fff'}
              strokeWidth={selectedNode === null || selectedNode === node ? 2 : 0.5}
              dot={chartData.length < 2}
              activeDot={{ r: 4 }}
              opacity={selectedNode === null || selectedNode === node ? 1 : 0.3}
            />
          ))}

          {/* Brush for zooming */}
          <Brush
            dataKey="time"
            height={20}
            stroke="#4b5563"
            fill="#1f2937"
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Statistics */}
      {showStatistics && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {resolvedNodeCodes.map((node) => (
            <div
              key={node}
              className="bg-gray-700 rounded p-2"
              style={{ borderLeft: `3px solid ${NODE_COLORS[node] || '#fff'}` }}
            >
              <div className="font-bold text-white">{node}</div>
              <div className="text-gray-400">
                Min: {formatPrice(statistics[node]?.min || 0)}
              </div>
              <div className="text-gray-400">
                Max: {formatPrice(statistics[node]?.max || 0)}
              </div>
              <div className="text-gray-400">
                Avg: {formatPrice(statistics[node]?.avg || 0)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Current prices table */}
      <div className="mt-4">
        <h4 className="text-sm font-bold text-gray-400 mb-2">Current Prices</h4>
        <div className="grid grid-cols-4 gap-2">
          {resolvedNodeCodes.map((node) => {
            const latestPrice = chartData[chartData.length - 1]?.[node];
            const previousPrice = chartData[chartData.length - 2]?.[node];
            const change = latestPrice && previousPrice ? latestPrice - previousPrice : 0;
            const changePercent = previousPrice ? (change / previousPrice) * 100 : 0;

            return (
              <div
                key={node}
                className="bg-gray-700 rounded p-2 text-center"
              >
                <div className="text-xs text-gray-400">{node}</div>
                <div className="text-lg font-bold text-white">
                  {latestPrice ? formatPrice(latestPrice) : 'N/A'}
                </div>
                {change !== 0 && (
                  <div
                    className={`text-xs ${
                      change > 0 ? 'text-red-400' : 'text-green-400'
                    }`}
                  >
                    {change > 0 ? '↑' : '↓'} {changePercent.toFixed(1)}%
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PriceChart;
